import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { describeStatus, type StatusKind } from '../statusText';
import type { PlatformMetricDefinition, SectionStatus } from '../types';

/** Small presentation pieces for the Operations and Risk Center. Each takes plain props, knows nothing about the data contract, and is meant to be replaced by the
 * shared dashboard primitives (src/shared/dashboard) once that work is merged to main. */

const ICONS: Record<StatusKind, string> = { good: '✓', attention: '!', critical: '✕', info: 'i' };

/** A status as an icon AND a word, never colour alone. */
export function StatusPill({ kind, children }: { kind: StatusKind; children: string }) {
  return (
    <span className={`pc-pill pc-pill--${kind}`}>
      <span className="pc-pill__icon" aria-hidden="true">{ICONS[kind]}</span>
      {children}
    </span>
  );
}

export function StatusFromServer({ value }: { value: string | null }) {
  // A null means the section behind it was unavailable: say so, never leave a blank that reads as "fine".
  if (value === null) return <span className="pc-muted">Unavailable</span>;
  const { kind, label } = describeStatus(value);
  return <StatusPill kind={kind}>{label}</StatusPill>;
}

/** "Data unavailable" with the reason. Never carries a number. */
export function Unavailable({ reason, title = 'Data unavailable' }: { reason: string | null | undefined; title?: string }) {
  return (
    <div className="pc-unavailable" data-testid="platform-unavailable">
      <span className="pc-unavailable__icon" aria-hidden="true">⚠</span>
      <div>
        <p className="pc-unavailable__title">{title}</p>
        {reason && <p className="pc-unavailable__reason">{reason}</p>}
      </div>
    </div>
  );
}

/** A small "i" button that explains one figure from the platform metric dictionary. Opens on click or Enter/Space, closes on Escape or when focus leaves. */
export function MetricInfo({ metric }: { metric: PlatformMetricDefinition | undefined }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!metric) return null;
  return (
    <span
      className="pc-info"
      ref={rootRef}
      onKeyDown={(event) => { if (event.key === 'Escape') setOpen(false); }}
      onBlur={(event) => { if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false); }}
    >
      <button type="button" className="pc-info__button" aria-label={`About ${metric.displayName}`} aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">i</span>
      </button>
      <span id={panelId} role="note" className="pc-info__panel" hidden={!open}>
        <strong>{metric.displayName}</strong>
        <span>{metric.definition}</span>
        <span><em>How it is calculated:</em> {metric.formula}</span>
        <span><em>Includes:</em> {metric.included}</span>
        <span><em>Leaves out:</em> {metric.excluded}</span>
        <span className="pc-muted">Source: {metric.sourceModule}</span>
      </span>
    </span>
  );
}

/** One section of the Center: its own heading, its own availability, and either its content or "Data unavailable" with the reason. */
export function PlatformSection({
  id, title, status, info, note, children,
}: {
  id: string;
  title: string;
  status: SectionStatus | null;
  info?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
}) {
  const headingId = `${id}-heading`;
  return (
    <section className="pc-card" aria-labelledby={headingId} data-testid={`platform-section-${id}`}>
      <h3 id={headingId} className="pc-card__title">{title}{info}</h3>
      {note && <p className="pc-note">{note}</p>}
      {status && !status.available ? <Unavailable reason={status.reason} /> : children}
    </section>
  );
}

export function Stat({ label, value, info, emphasis }: { label: string; value: number | string; info?: ReactNode; emphasis?: StatusKind | undefined }) {
  return (
    <div className={`pc-stat${emphasis ? ` pc-stat--${emphasis}` : ''}`}>
      <dt className="pc-stat__label">{label}{info}</dt>
      <dd className="pc-stat__value">{value}</dd>
    </div>
  );
}

/** A horizontal bar per item plus the same numbers as a table, so no chart is the only way to read a figure. */
export function BarList({ caption, items }: { caption: string; items: { label: string; value: number }[] }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <>
      <ul className="pc-bars" aria-label={caption}>
        {items.map((item) => (
          <li key={item.label}>
            <span className="pc-bars__label">{item.label}</span>
            <span className="pc-bars__track" aria-hidden="true"><span className="pc-bars__fill" style={{ width: `${(item.value / max) * 100}%` }} /></span>
            <span className="pc-bars__value">{item.value}</span>
          </li>
        ))}
      </ul>
      <details className="pc-table-alt">
        <summary>View as table</summary>
        <table>
          <caption>{caption}</caption>
          <thead><tr><th scope="col">Item</th><th scope="col" className="pc-num">Companies or items</th></tr></thead>
          <tbody>{items.map((i) => <tr key={i.label}><th scope="row">{i.label}</th><td className="pc-num">{i.value}</td></tr>)}</tbody>
        </table>
      </details>
    </>
  );
}
